import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { portfolioConfig } from '../config/portfolio';
import { Icon } from './Icons';

const LazyProjectModal = lazy(() =>
  Promise.resolve({
    default: ({ project, onClose, projectDetails }) => {
      const [isVideoPlaying, setIsVideoPlaying] = useState(false);
      const videoRef = useRef(null);

      useEffect(() => {
        if (isVideoPlaying && videoRef.current) {
          const scrollTop = document.querySelector('.fixed.inset-0')?.scrollTop || 0;
          videoRef.current.scrollIntoView({ block: 'nearest' });
          document.querySelector('.fixed.inset-0').scrollTop = scrollTop;
        }
      }, [isVideoPlaying]);

      return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <img
                src={project.image}
                alt={project.name}
                className="w-full h-auto object-fit rounded-t-2xl"
                loading="lazy"
              />
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <Icon name="close" />
              </button>
            </div>

            <div className="p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-white">{project.name}</h3>
                <div className="flex gap-2">
                  {project.categories.map((category, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-medium whitespace-nowrap"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>

              <div className="prose prose-invert max-w-none mb-6 overflow-y-auto">
                <div
                  className="prose prose-invert max-w-none mb-6"
                  dangerouslySetInnerHTML={{
                    __html:
                      projectDetails[project.id] ||
                      '<div class="text-center py-8 text-gray-400">Loading...</div>',
                  }}
                />
              </div>

              <div className="mb-6">
                <div className="inline-block text-lg font-semibold text-white mb-12 flex items-center gap-2">
                  <Icon name="code" className="mt-1" />
                  <h4 className="mr-12 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                    Technologies
                  </h4>
                  <span className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                {project.github ? (
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105"
                  >
                    <Icon name="github" />
                    View Code
                  </a>
                ) : (
                  <button
                    disabled
                    className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-gray-300 font-medium rounded-lg cursor-not-allowed"
                  >
                    <Icon name="github" />
                    Sourcecode Private
                  </button>
                )}

                {project.liveDemo ? (
                  <a
                    href={project.liveDemo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-black font-medium rounded-lg transition-all duration-200 transform hover:scale-105"
                  >
                    <Icon name="external-link" />
                    Live Demo
                  </a>
                ) : (
                  <button
                    disabled
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-800 to-red-500 text-white font-medium rounded-lg cursor-not-allowed"
                  >
                    <Icon name="offline" />
                    Offline
                  </button>
                )}
              </div>

              {project.demoVideo && (
                <div className="mb-6 mt-12">
                  <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Icon name="video" className="mt-1" />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                      Demo Video
                    </span>
                  </h4>

                  <div className="aspect-video rounded-lg overflow-hidden relative bg-black flex items-center justify-center">
                    {!isVideoPlaying ? (
                      <button
                        onClick={() => setIsVideoPlaying(true)}
                        className="absolute z-10 bg-cyan-500 hover:bg-cyan-600 text-black p-4 rounded-full shadow-lg transition flex items-center justify-center"
                        aria-label="Play demo video"
                      >
                        <Icon name="play" />
                      </button>
                    ) : project.demoVideo.endsWith('.mp4') ? (
                      <video
                        controls
                        autoPlay
                        className="w-full h-full object-fit rounded-lg"
                        src={project.demoVideo}
                        ref={videoRef}
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <iframe
                        src={project.demoVideo}
                        title={`${project.name} Demo`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    },
  })
);

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('Complete');
  const [selectedProject, setSelectedProject] = useState(null);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [projectDetails, setProjectDetails] = useState({});
  const [isIntroVisible, setIsIntroVisible] = useState(false);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [visibleProjects, setVisibleProjects] = useState([]);

  const categories = ['Ongoing', 'Complete', 'Practice'];

  useEffect(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      setFilteredProjects(
        portfolioConfig.projects.filter((project) => {
          if (selectedCategory === 'Practice')
            return project.categories.includes('Practice');
          if (selectedCategory === 'Ongoing')
            return project.categories.includes('Ongoing');
          if (selectedCategory === 'Complete')
            return (
              !project.categories.includes('Practice') &&
              !project.categories.includes('Ongoing')
            );
          return false;
        })
      );
      setIsTransitioning(false);
      setVisibleProjects([]); // reset visibility for new filtered list
    }, 150);
  }, [selectedCategory]);

  useEffect(() => {
    const introEl = document.getElementById('projects-intro');
    const filtersEl = document.getElementById('projects-filters');
    const projectEls = document.querySelectorAll('.project-item');

    if (!introEl || !filtersEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target.id === 'projects-intro') {
              setIsIntroVisible(true);
            } else if (entry.target.id === 'projects-filters') {
              setIsFiltersVisible(true);
            } else if (entry.target.classList.contains('project-item')) {
              const index = Number(entry.target.dataset.index);
              setVisibleProjects((prev) => {
                if (!prev.includes(index)) return [...prev, index];
                return prev;
              });
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(introEl);
    observer.observe(filtersEl);
    projectEls.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [filteredProjects]);

  const loadDetailForProject = async (projectId) => {
    try {
      const response = await fetch(`/project-details/${projectId}.txt`);
      if (!response.ok) throw new Error('File not found');
      const text = await response.text();
      setProjectDetails((prev) => ({ ...prev, [projectId]: text }));
    } catch (error) {
      console.error(`Error loading details for project ${projectId}:`, error);
      setProjectDetails((prev) => ({ ...prev, [projectId]: null }));
    }
  };

  const preloadAssets = (project) => {
    if (!projectDetails[project.id]) {
      loadDetailForProject(project.id);
    }

    if (project.demoVideo && project.demoVideo.endsWith('.mp4')) {
      const video = document.createElement('video');
      video.src = project.demoVideo;
      video.preload = 'metadata';
    }

    const img = new Image();
    img.src = project.image;
  };

  return (
    <section id="projects" className="py-20 section-bg-2 relative">
      <div className="max-w-7xl mx-auto px-8">
        {/* Intro */}
        <div
          id="projects-intro"
          className={`text-center mb-8 transition-all duration-1000 ease-out ${
            isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            My{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Projects
            </span>
          </h2>
        </div>

        {/* Category Filters */}
        <div
          id="projects-filters"
          className={`flex flex-wrap justify-center gap-4 mb-12 transition-all duration-1000 ease-out ${
            isFiltersVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-200 transform hover:scale-105 ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-black shadow-lg shadow-cyan-500/30'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-cyan-400'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div
          className={`transition-all duration-300 flex flex-wrap justify-center gap-8 ${
            isTransitioning ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
          }`}
        >
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              data-index={index}
              onMouseEnter={() => preloadAssets(project)}
              className={`project-item w-full sm:w-[45%] lg:w-[30%] group relative bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700 hover:border-cyan-400/50 transition-all duration-700 transform hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/10 ${
                visibleProjects.includes(index)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <div className="relative overflow-hidden">
                <img
                  src={project.image}
                  alt={project.name}
                  className="w-full h-64 object-fit group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute inset-0 flex items-center justify-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={async () => {
                      if (!projectDetails[project.id])
                        await loadDetailForProject(project.id);
                      setSelectedProject(project);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-black/70 backdrop-blur-sm text-white rounded-lg font-medium hover:bg-black/80 transition-colors"
                  >
                    <Icon name="eye" />
                    View Details
                  </button>
                  {project.liveDemo ? (
                    <a
                      href={project.liveDemo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-black backdrop-blur-sm rounded-lg font-medium transition-colors"
                    >
                      <Icon name="external-link" />
                      Live Demo
                    </a>
                  ) : (
                    <button
                      disabled
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-800 to-red-500 text-white rounded-lg font-medium cursor-not-allowed"
                    >
                      <Icon name="offline" />
                      Offline
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors flex-1">
                    {project.name}
                  </h3>
                  <div className="flex flex-wrap gap-1 ml-2">
                    {project.categories.map((category, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-xs font-medium whitespace-nowrap"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>

                <p className="text-gray-300 mb-4 text-sm leading-relaxed">
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
            </div>
          ))}
        </div>

        {selectedProject && (
          <Suspense fallback={<div className="fixed inset-0 z-50 bg-black/80 text-white flex items-center justify-center">Loading modal...</div>}>
            <LazyProjectModal
              project={selectedProject}
              onClose={() => setSelectedProject(null)}
              projectDetails={projectDetails}
            />
          </Suspense>
        )}
      </div>
    </section>
  );
};

export default Projects;